import type { Writable } from '@eturi/ts-helpers'
import { notEmpty, pick } from '@eturi/util'
import { keysToInt } from '@op/util'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { createSliceTransformer, stripPayload } from 'rtk-slice-transformer'
import { resetAction } from '../actions/index.js'
import { fetchSystemInfo } from '../thunks/index.js'
import type { Lang } from '../types/Lang.js'
import type {
	Onboarding,
	OnboardingClientData,
	OnboardingConcern,
	OnboardingSignupChoices,
	OnboardingSignupUser,
	OnboardingUser,
} from '../types/Onboarding.js'
import {
	filterOnboardingByAge,
	findOnboardingByAge,
	getOnboardingLabel,
} from '../types/Onboarding.js'
import type { SThunk, SThunkState } from '../types/SThunk.js'

type OnboardingState = {
	readonly data: OnboardingClientData
	readonly onboarding: Maybe<Onboarding>
	readonly version: string
}

export type WithOnboardingState = {
	readonly onboarding: OnboardingState
}

const initialState: OnboardingState = {
	data: { concerns: [], users: {} },
	onboarding: null,
	version: '',
}

export const onboardingSlice = /*@__PURE__*/ createSlice({
	name: 'onboarding',
	initialState,
	reducers: {
		addOnboardingConcern(s, { payload: concern }: PayloadAction<OnboardingConcern>) {
			if (!hasConcern(s, concern)) {
				s.data.concerns.push(concern)
			}
		},

		addOnboardingUser(s, { payload: user }: PayloadAction<OnboardingUser>) {
			if (!hasUser(s, user)) {
				s.data.users[user.name] = user
			}
		},

		removeOnboardingConcern(s, { payload: concern }: PayloadAction<OnboardingConcern>) {
			s.data.concerns = s.data.concerns.filter((c) => c.id !== concern.id)
		},

		removeOnboardingUser(s, { payload: user }: PayloadAction<OnboardingUser>) {
			delete s.data.users[user.name]
		},

		resetOnboarding() {
			return initialState
		},
	},

	extraReducers: (builder) =>
		builder
			.addCase(resetAction, () => initialState)
			.addCase(fetchSystemInfo.fulfilled, (s, a) => {
				if (a.payload.onboarding_choices) {
					const { onboarding, version } = a.payload.onboarding_choices
					s.onboarding = onboarding
					s.version = version
				}
			}),
})

export const {
	addOnboardingConcern,
	addOnboardingUser,
	removeOnboardingConcern,
	removeOnboardingUser,
	resetOnboarding,
} = onboardingSlice.actions

const isUserAction = /*@__PURE__*/ isAnyOf(addOnboardingUser, removeOnboardingUser)

export const onboardingSliceTransformer = /*@__PURE__*/ createSliceTransformer(
	onboardingSlice,
	(s) => ({
		...s,
		data: {
			concerns: s.data.concerns.map((c) => c.id),
			...keysToInt(s.data, ['users']),
		},
	}),
	(a) => (isUserAction(a) ? stripPayload(a) : a),
)

const hasConcern = (s: OnboardingState, concern: OnboardingConcern) =>
	s.data.concerns.some((c) => c.id === concern.id)

const hasUser = (s: OnboardingState, user: OnboardingUser) => s.data.users[user.name] != null

////////// Thunks //////////////////////////////////////////////////////////////

type OnboardingThunkState = SThunkState & WithOnboardingState

/**
 * This thunk fetches the onboarding choices from the state and maps them to a signup choices
 * object. If the onboarding choices are not available, it returns null.
 *
 * @param lang - The language to use for the signup choices
 * @returns The signup choices if available, otherwise null
 */
export const getOnboardingSignupChoices =
	(lang: Lang): SThunk<OnboardingSignupChoices | null, OnboardingThunkState> =>
	(_, getState) => {
		const {
			data: { concerns, users },
			onboarding,
			version,
		} = getState().onboarding

		if (!(onboarding && version && (notEmpty(concerns) || notEmpty(users)))) return null

		return mapOnboardingSignupChoices(concerns, Object.values(users), onboarding, version, lang)
	}

/**
 * This single helper maps client data and the Onboarding schema to a signup choices object.
 * Having it here is useful, but it will need to be modified later if things become user-editable.
 */
const mapOnboardingSignupChoices = (
	concerns: OnboardingConcern[],
	users: OnboardingUser[],
	onboarding: Onboarding,
	version: string,
	lang: Lang,
): OnboardingSignupChoices => ({
	concerns: { selected: concerns.map((c) => c.id) },
	state: 'active',
	type: 'onboarding_choices',
	users: Object.values(users).map((user) => mapOnboardingSignupUser(user, onboarding, lang)),
	version,
})

const mapOnboardingSignupUser = (
	{ age, name: user_name, time_zone }: OnboardingUser,
	onboarding: Onboarding,
	lang: Lang,
) => {
	// Start with defaults and required fields
	const onboardingSignupUser: Writable<OnboardingSignupUser> = {
		age,
		time_zone,
		user_name,

		// Setting filters to true by default. These don't appear in `Onboarding` schema.
		content_filter: {
			filter_adult_content: true,
			filter_blocked_apps: true,
		},

		// Setting this to true by default
		geolocation: {
			user_location_active: true,
		},
	}

	const ageAppropriateAllowance = findOnboardingByAge(onboarding.allowance, age)

	if (ageAppropriateAllowance) {
		onboardingSignupUser.allowance = {
			allowance: ageAppropriateAllowance.allowance,
			allowance_enabled: ageAppropriateAllowance.allowance_enabled ?? false,
		}
	}

	const ageAppropriateAppRules = filterOnboardingByAge(onboarding.app_rules, age)

	if (ageAppropriateAppRules.length) {
		onboardingSignupUser.app_rules = ageAppropriateAppRules.map((rule) =>
			pick(rule, ['id', 'directive']),
		)
	}

	const ageAppropriateGeofences = filterOnboardingByAge(onboarding.geofences, age)

	if (ageAppropriateGeofences.length) {
		onboardingSignupUser.geofences = ageAppropriateGeofences.map((geofence) => ({
			incomplete: true,
			name: getOnboardingLabel(geofence.label, lang),
			triggers: ['entry', 'exit'],
		}))
	}

	const ageAppropriateSchedules = filterOnboardingByAge(onboarding.schedules, age)

	if (ageAppropriateSchedules.length) {
		onboardingSignupUser.schedules = ageAppropriateSchedules.map(
			({ active, days, rule_name, time_duration }) => ({
				active: active ?? false,
				days,
				rule_name: getOnboardingLabel(rule_name, lang),
				time_duration,
			}),
		)
	}

	const ageAppropriateVew = filterOnboardingByAge(onboarding.view, age)

	if (ageAppropriateVew.length) {
		onboardingSignupUser.view = {
			view_enabled: ageAppropriateVew.some((v) => v.view_enabled ?? false),
		}
	}

	return onboardingSignupUser
}
